import React from "react";
import T from "prop-types";
import { get, isEmpty } from "lodash";
import { NoteTooltip as IconTooltip } from "MODULES/shared/components";
import { Link } from "react-router-dom";
import NoteTooltip from "MODULES/shared/components/NoteTooltip/NoteTooltip";
import { dollars } from "LIB/formatters/currency";
import { MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION } from "APP_ROOT/constants";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";
import { REPAIRED } from "../../../inventories/constants";

export function RepairingIcon({ inventory }) {
  const isManagedRepair =
    inventory.managed_repair_repaired || inventory.managed_repair_in_progress;
  if (
    (inventory.repair_status && inventory.repair_status !== REPAIRED) ||
    isManagedRepair
  ) {
    const repairOrderId = get(
      inventory,
      "repair_order_line_items[0].repair_order_id"
    );

    const awb = get(
      inventory,
      "repair_order_line_items[0].incoming_awb_number"
    );

    const carrier = get(
      inventory,
      "repair_order_line_items[0].return_to_shipping_method.carrier"
    );

    const isIncoming = !isEmpty(awb);

    const approvalStatusRaw = get(
      inventory,
      "repair_order_line_items[0].approval_status"
    );

    const approvalStatus = approvalStatusRaw
      ? approvalStatusRaw
          .replace(/_/g, " ") // Replace underscores with spaces
          .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize each word
      : "";

    const text = isManagedRepair
      ? "Managed Repair In Progress"
      : `In Repair Process ${approvalStatus}`;

    const icon = isManagedRepair ? "fa-recycle" : "fa-wrench";

    if (!repairOrderId) {
      return <IconTooltip icon={icon} note={text} color={"#FFFFFF"} />;
    }

    return (
      <Link to={`/repair-orders/${repairOrderId}`}>
        <IconTooltip
          icon={icon}
          placement={"top"}
          note={
            isIncoming
              ? `Returning from Repair Process<br />${carrier
                  ? `${carrier.toUpperCase()} `
                  : ""}AWB# ${awb}`
              : text
          }
          color={"#FFFFFF"}
        />
      </Link>
    );
  }

  return null;
}

RepairingIcon.propTypes = {
  inventory: T.shape({
    repair_status: T.string.required
  })
};

const getIconColor = inventory => {
  switch (inventory.listing_override) {
    case true:
      return "#4caf50";
    case false:
      return "red";
    default:
      return "inherit";
  }
};

const getListingText = listingOverride => {
  switch (listingOverride) {
    case true:
      return "Listing Override: LIST FOR SALE";
    case false:
      return "Listing Override: DO NOT LIST";
    default:
      return "";
  }
};

const RenderListingOverrideIcon = ({ inventory }) => {
  const iconColor = getIconColor(inventory);
  const text = getListingText(inventory.listing_override);

  switch (inventory.listing_override) {
    case true:
    case false:
      return <NoteTooltip color={iconColor} icon="fa-registered" note={text} />;
    default:
      return null;
  }
};

const buildNote = (inventory, minOutrightValue) => {
  const notes = [];

  if (inventory.do_not_exchange) {
    notes.push(`Do Not Exchange - Line Item Restriction`);
  }
  if (inventory.do_not_exchange_due_to_outright_value) {
    notes.push(
      `Do Not Exchange - Outright Value Under ${dollars(minOutrightValue)}`
    );
  }
  if (inventory.part.pass_fail) {
    notes.push("Do Not Exchange - Due to Pass Fail");
  }

  return notes.join("<br />");
};

const ConsolidatedIconTooltip = ({ inventory }) => {
  const settings = useSettings();
  const minOutrightValue =
    settings[MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION];
  const note = buildNote(inventory, minOutrightValue);
  const condition = inventory.condition;

  return note && condition !== "as_removed" ? (
    <IconTooltip
      icon="fa-times-circle-o"
      note={note}
      color="#f44336"
      placement="top"
    />
  ) : null;
};

const InventoryListingOverrideCell = ({ inventory, renderDummyValues }) => {
  const doNotSellNote = `DO NOT SELL!${inventory.do_not_sell_reason
    ? ` ${inventory.do_not_sell_reason}`
    : ""}`;

  return (
    <div>
      {!renderDummyValues &&
        inventory.reserve_for_seller_id && (
          <IconTooltip
            icon={"fa-lock"}
            placement={"top"}
            note={"Reserved for Sale"}
            color={"#FFFFFF"}
          />
        )}{" "}
      {!renderDummyValues &&
        inventory.active_transfer && (
          <IconTooltip
            icon={"fa-plane"}
            placement={"top"}
            note={"Store Transfer"}
            color={"#FFFFFF"}
          />
        )}{" "}
      {!renderDummyValues && <RepairingIcon inventory={inventory} />}{" "}
      {!renderDummyValues &&
        inventory.unsellable_per_user && (
          <IconTooltip
            icon="fa-diamond"
            placement="top"
            note={doNotSellNote}
            color="#ecb100"
          />
        )}
      {inventory.status === "out_on_exchange" && (
        <IconTooltip
          icon={"fa-exchange"}
          placement={"top"}
          note={"Out on Exchange"}
          color={"#FFFFFF"}
        />
      )}
      {renderDummyValues && (
        <IconTooltip icon="fa-copy" note="Dummy Stock Line" />
      )}
      <ConsolidatedIconTooltip inventory={inventory} />
      <RenderListingOverrideIcon inventory={inventory} />
    </div>
  );
};

InventoryListingOverrideCell.propTypes = {
  inventory: T.shape({}).isRequired,
  renderDummyValues: T.bool
};

export default InventoryListingOverrideCell;



// WEBPACK FOOTER //
// ./app/modules/shared/components/InventoryListingOverrideCell/InventoryListingOverrideCell.jsx